import { ConfirmModal } from "@components/ConfirmModal";
import { Loading } from "@components/Loading";
import { TopNav } from "@components/TopNav";
import { TeamTable } from "@components/Table";
import { deleteDoc, doc, getDocs, updateDoc, collection, query, where } from "@firebase/firestore";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "@helpers/useTranslation";
import { FirebaseContext } from "src/helpers/firebase";
import { useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { useToast } from "src/helpers/useToast";
import { ImagePicker } from "@components/ImagePicker";
import { Input } from "@components/Input";
import { useForm } from "react-hook-form";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useEvent } from "src/helpers/useEvent";
import { OracleAssociates } from "./OracleAssociates";

export const ManageTeams = () => {
  const { t } = useTranslation("admin");

  const eventId = useEventId();
  const { firestore, functions, storage } = useContext(FirebaseContext);
  const [event] = useEvent();
  const [teams, tLoading] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTeamIdToDelete, setActiveTeamIdToDelete] = useState<string | undefined>();
  const [image, setImage] = useState<undefined | File>(undefined);
  const [creating, setCreating] = useState(false);
  const [createNewTeam, _, error] = useHttpsCallable(functions, "createNewTeamV2");

  useEffect(() => {
    if (error) {
      toast(t("somethingWentWrong"), "🤷‍♂️", "error");
      console.error(error);
    }
  }, [error]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      teamName: "",
      password: "",
    },
  });

  const confirmDeleteTeam = (teamId: string) => {
    setShowConfirmModal(true);
    setActiveTeamIdToDelete(teamId);
  };

  const addPoints = async (teamId: string) => {
    const team = teams.find((team) => team.id === teamId);
    if (!team) return;

    const currentPoints = team.points;
    const additionalPoints = team.additionalPoints || 0;

    const teamDocRef = doc(firestore, `events/${eventId}/teams`, teamId);
    await updateDoc(teamDocRef, {
      additionalPoints: additionalPoints + 10,
      points: currentPoints + 10,
    });
  };

  const removePoints = async (teamId: string) => {
    const team = teams.find((team) => team.id === teamId);
    if (!team) return;

    const currentPoints = team.points;
    const additionalPoints = team.additionalPoints || 0;

    const teamDocRef = doc(firestore, `events/${eventId}/teams`, teamId);

    await updateDoc(teamDocRef, {
      additionalPoints: additionalPoints - 10,
      points: currentPoints - 10,
    });
  };

  const toast = useToast();

  const onDelete = async () => {
    try {
      // Remove all solutions docs (effectively removing the sub-collection) from this team
      const solution = await getDocs(
        collection(firestore, `events/${eventId}/teams/${activeTeamIdToDelete}/solutions`),
      );
      solution.forEach((solution) => deleteDoc(solution.ref));

      // Finally remove the team doc itself
      const teamDocRef = doc(firestore, `events/${eventId}/teams`, activeTeamIdToDelete);
      await deleteDoc(teamDocRef);
      toast(t("deleted"), "👍");
    } catch (error) {
      toast(t("problemOccured"), "", "error");
      console.error(error);
    }

    setActiveTeamIdToDelete(undefined);
  };

  const onCreateTeam = async (data: { teamName: string; password: string }) => {
    try {
      setCreating(true);
      const { teamName, password } = data;

      const displayName = teamName;
      const sanitisedName = teamName.replace(/[^a-zA-Z0-9]/g, "");

      // Check if team name already exists
      const teamsRef = collection(firestore, `events/${eventId}/teams`);
      const toSearchableString = (str: string): string => str.trim().toLowerCase().replace(/\s/g, "");
      const q = query(
        teamsRef,
        where("nameLowercase", "==", toSearchableString(teamName)),
        where("isUploader", "==", false),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        toast(t("teamNameAlreadyExists"), "🤷‍♂️", "error");
        return;
      }
      const { data: teamId } = await createNewTeam({
        eventId,
        teamName: sanitisedName,
        displayName,
        password,
      });

      // Upload team avatar if provided
      if (image) {
        const imagePath = `events/${eventId}/teams/${teamId}/avatar`;
        const imageRef = storageRef(storage, imagePath);
        await uploadBytes(imageRef, image);
      }

      toast(t("teamCreated"), "👍");
      setShowCreateModal(false);
      reset();
      setImage(undefined);
    } catch (error) {
      console.error(error);
      toast(t("problemOccured"), "", "error");
    } finally {
      setCreating(false);
    }
  };

  const noTeamsYet = teams.length === 0;

  return (
    <>
      <ConfirmModal
        messageCopy={t("areYouSureDeleteTeam")}
        show={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={onDelete}
      />
      {showCreateModal && (
        <div className="modal modal-open">
          <div className="modal-box relative">
            <button
              className="btn-sm btn-circle btn absolute right-2 top-2"
              onClick={() => {
                setShowCreateModal(false);
                reset();
                setImage(undefined);
              }}
            >
              ✕
            </button>
            <h3 className="text-lg font-bold">{t("createTeam")}</h3>
            <p className="my-4 text-sm text-gray-500">{t("aboutCreateTeam")}</p>
            <form onSubmit={handleSubmit(onCreateTeam)} className="mt-4">
              <div className="form-control w-full">
                <Input
                  label={t("teamName")}
                  name="teamName"
                  required
                  placeholder={t("teamNamePlaceholder")}
                  requiredMessage={t("fieldRequired")}
                  errors={errors}
                  register={register}
                />
              </div>
              <div className="form-control mt-4 w-full">
                <Input
                  label={t("teamPassword")}
                  name="password"
                  type="password"
                  placeholder={t("passwordPlaceholder")}
                  errors={errors}
                  register={register}
                  tooltip={t("teamPasswordTooltip")}
                />
              </div>
              <ImagePicker allowUpload label={t("teamPhoto")} uploadLabel={t("uploadPhoto")} onNewImage={setImage} />
              <div className="mb-4 flex justify-center">
                <button type="submit" disabled={creating} className={`btn-primary btn ${creating ? "loading" : ""}`}>
                  {t("createTeam")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <TopNav isAdmin backLink="/admin" />
      <main className="flex flex-col items-center">
        <div className="mb-24 flex w-full flex-col items-center py-4 px-6">
          <Loading loading={tLoading}>
            <div className="flex w-full max-w-6xl flex-col">
              <h1 className="mt-12 mb-8 text-center text-4xl font-semibold">{t("manageTeams")}</h1>
              <p className="mt-16 mb-8 text-center text-lg font-light">{t("aboutManageTeams")}</p>
              <div className="mb-2 flex justify-end pt-4 sm:mx-8 sm:px-4">
                <button onClick={() => setShowCreateModal(true)} className="btn-primary btn-sm btn">
                  {t("createTeam")}
                </button>
              </div>
              {noTeamsYet && <div className="text-center text-xl font-semibold">{t("noTeamsYet")}</div>}
              {!noTeamsYet && (
                <TeamTable
                  data={teams}
                  onAddPoints={addPoints}
                  onRemovePoints={removePoints}
                  onDeleteTeam={confirmDeleteTeam}
                />
              )}
              {event?.mainConfig?.oracleMode && <OracleAssociates />}
            </div>
          </Loading>
        </div>
      </main>
    </>
  );
};
